import { Box, Button, SimpleGrid, Text, Flex, Divider } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { FormikInput, FormikSelect } from 'components/Formik';
import { routePaths } from 'global/routePaths';
import { useCheckout } from 'hooks/accountBilling/useCheckout';
import { useFetchCountry } from 'hooks/accountBilling/useFetchCountry';
import { useCheckoutStore } from 'store/useCheckoutStore';

import { monthOptions, yearOptions } from '../options';
import { initialCheckoutValue } from './schema';
import { paymentFormValidationSchema } from './transformCheckoutDataToFormdata';
interface IProps {
  duration_type: string;
  plan_name: string;
  formData?: typeof initialCheckoutValue;
}

const CheckoutForm: React.FC<IProps> = ({ duration_type, plan_name, formData = initialCheckoutValue }) => {
  const { t } = useTranslation(['common', 'accountBilling']);

  const navigate = useNavigate();

  const { status, mutateAsync } = useCheckout();

  const { checkoutDetails, clearCheckoutDetails } = useCheckoutStore(state => state);

  const { status: fetchCountryStatus, data: countryData } = useFetchCountry();

  const formatCreditCardNum = (ccNum: string) => {
    return ccNum.replaceAll('-', '');
  };

  const handleFormSubmit = (values: typeof initialCheckoutValue) => {
    const postData = {
      number: formatCreditCardNum(values.number),
      card_name: values.card_name,
      expiry_month: values.expiry_month.value,
      expiry_year: values.expiry_year.value,
      cvc: values.cvc,
      country: values.country?.iso || '',
      city: values.city,
      state: values.state,
      address_1: values.address_1,
      address_2: values.address_2,
      zip_code: values.zip_code,
      plan: plan_name,
      // Duration type is either 'annual' or 'monthly'
      duration: duration_type,
      add_ons: checkoutDetails.addOns.map(addOn => addOn?.id) || [],
    };

    mutateAsync(postData).then(() => {
      navigate(routePaths.accountBilling.base.route);
      window.location.reload();
    });
  };

  const handleCancellation = () => {
    clearCheckoutDetails();
    navigate(routePaths.accountBilling.upgrade);
  };

  const creditCardFormat = (
    value: any,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
    name: string
  ) => {
    const v = value
      .replace(/\s+/g, '')
      .replace(/[^0-9]/gi, '')
      .substr(0, 16);
    const parts = [];

    for (let i = 0; i < v.length; i += 4) {
      parts.push(v.substr(i, 4));
    }

    setFieldValue(name, parts.length > 1 ? parts.join('-') : value);
  };

  return (
    <Box bg="newGray.100">
      <Formik
        enableReinitialize
        initialValues={formData}
        validationSchema={paymentFormValidationSchema}
        onSubmit={handleFormSubmit}
      >
        {({ setFieldValue }) => (
          <Form noValidate>
            <Box display="flex" flexDirection="column" maxWidth={['100%', null, '450px']}>
              {/* Payment Method starts */}
              <Text mb={4} fontWeight={600}>
                {t('accountBilling:paymentMethod')}
              </Text>
              <FormikInput
                type="text"
                name="number"
                label={t('accountBilling:cardNumber')}
                inputProps={{
                  placeholder: 'xxxx-xxxx-xxxx-xxxx',
                  bg: 'white',
                }}
                formControlProps={{ isRequired: true }}
                labelProps={{
                  fontWeight: 300,
                  color: 'newGray.1000',
                }}
                afterChangeCallback={e => creditCardFormat(e.target.value, setFieldValue, 'number')}
              />
              <SimpleGrid templateColumns="1.5fr 0.5fr" spacing="2">
                <Flex gridGap={2}>
                  <FormikSelect
                    name="expiry_month"
                    options={monthOptions}
                    label={t('accountBilling:expiryMonth')}
                    selectProps={{
                      placeholder: '01',
                      isClearable: false,
                    }}
                    labelProps={{
                      fontWeight: 300,
                      color: 'newGray.1000',
                    }}
                  />
                  <FormikSelect
                    name="expiry_year"
                    options={yearOptions}
                    label={t('accountBilling:expiryYear')}
                    selectProps={{
                      // placeholder: t('accountBilling:selectYear'),
                      placeholder: new Date().getFullYear(),
                      isClearable: false,
                    }}
                    labelProps={{
                      fontWeight: 300,
                      color: 'newGray.1000',
                    }}
                  />
                </Flex>
                <FormikInput
                  type="text"
                  name="cvc"
                  label={t('accountBilling:cvvCode')}
                  inputProps={{
                    placeholder: '•••',
                    bg: 'white',
                  }}
                />
              </SimpleGrid>
              <FormikInput
                type="text"
                name="card_name"
                label={t('accountBilling:nameOnCard')}
                inputProps={{
                  placeholder: t('accountBilling:enterCardHoldersName'),
                  bg: 'white',
                }}
                formControlProps={{ isRequired: true }}
                labelProps={{
                  fontWeight: 300,
                  color: 'newGray.1000',
                }}
              />

              {/* Billing Address starts */}
              <Divider mb={4} borderStyle="dashed" borderColor="newGray.500" />
              <Text mb={4} fontWeight={600}>
                {t('accountBilling:billingAddress')}
              </Text>

              <FormikInput
                type="text"
                name="address_1"
                label={t('accountBilling:addressLine1')}
                labelProps={{
                  fontWeight: 300,
                  color: 'newGray.1000',
                }}
                inputProps={{
                  bg: 'white',
                }}
              />
              <FormikInput
                type="text"
                name="address_2"
                label={t('accountBilling:addressLine2')}
                labelProps={{
                  fontWeight: 300,
                  color: 'newGray.1000',
                }}
                inputProps={{
                  bg: 'white',
                }}
              />
              <SimpleGrid columns={2} spacing={2}>
                <FormikInput
                  type="text"
                  name="city"
                  label={t('common:city')}
                  labelProps={{
                    fontWeight: 300,
                    color: 'newGray.1000',
                  }}
                  inputProps={{
                    bg: 'white',
                  }}
                />
                <FormikInput
                  type="text"
                  name="state"
                  label={t('common:state')}
                  labelProps={{
                    fontWeight: 300,
                    color: 'newGray.1000',
                  }}
                  inputProps={{
                    bg: 'white',
                  }}
                />
              </SimpleGrid>

              <SimpleGrid columns={2} spacing={2}>
                <FormikSelect
                  name="country"
                  options={countryData?.payload || []}
                  label={t('common:country')}
                  selectProps={{
                    placeholder: t('accountBilling:selectCountry'),
                    isLoading: fetchCountryStatus === 'loading',
                    getOptionLabel: (option: any) => option.name,
                    getOptionValue: (option: any) => option.iso,
                    isClearable: false,
                  }}
                  labelProps={{
                    fontWeight: 300,
                    color: 'newGray.1000',
                  }}
                />
                <FormikInput
                  type="text"
                  name="zip_code"
                  label="zip code"
                  labelProps={{
                    fontWeight: 300,
                    color: 'newGray.1000',
                  }}
                  inputProps={{
                    bg: 'white',
                  }}
                />
              </SimpleGrid>
            </Box>
            <Box display="flex" flexDirection="column" maxWidth={['100%', null, '450px']}>
              <Text>
                {t('accountBilling:paymentsWillAutorenewEachMonthOrYearDependingOnYourPlanYouMayCancelAtAnyTime')}
                <Text as="span" fontWeight={700}>
                  {' '}
                  {t('accountBilling:paymentsAreNotRefundable')}
                </Text>
              </Text>
            </Box>
            <Flex justifyContent="flex-end" alignItems="center" gridGap={4}>
              <Button colorScheme="secondary" variant="outline" onClick={handleCancellation}>
                {t('common:cancel')}
              </Button>
              <Button
                colorScheme="secondary"
                h="45px"
                w={{ base: '100%', md: '50%' }}
                borderRadius={4}
                color="white"
                my={6}
                isLoading={status === 'loading'}
                type="submit"
              >
                {t('accountBilling:checkout')}
              </Button>
            </Flex>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default CheckoutForm;
